export type SupportedLocale = 'tr-TR' | 'en-US';

export function resolveSupportedLocale(language: string | undefined | null): SupportedLocale {
  return language?.toLowerCase().includes('tr') ? 'tr-TR' : 'en-US';
}


export function getPrimeDateFormat(locale: SupportedLocale): string {
  return locale === 'tr-TR' ? 'dd.mm.yy' : 'mm/dd/yy';
}

export function getDatePlaceholder(locale: SupportedLocale): string {
  return locale === 'tr-TR' ? 'GG.AA.YYYY' : 'MM/DD/YYYY';
}

export function parseLocaleDate(
  value: string | Date | null | undefined,
  locale: SupportedLocale,
): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  }

  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return null;
  }

  const pattern =
    locale === 'tr-TR'
      ? /^(\d{2})\.(\d{2})\.(\d{4})$/
      : /^(\d{2})\/(\d{2})\/(\d{4})$/;

  const match = pattern.exec(trimmed);
  if (!match) {
    return null;
  }

  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]);

  const day = locale === 'tr-TR' ? first : second;
  const month = locale === 'tr-TR' ? second : first;

  return buildValidDate(year, month, day);
}

export function formatLocaleDate(date: Date, locale: SupportedLocale): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());

  return locale === 'tr-TR' ? `${day}.${month}.${year}` : `${month}/${day}/${year}`;
}

export function getLocaleDateSeparator(locale: SupportedLocale): string {
  return locale === 'tr-TR' ? '.' : '/';
}

export function formatLocaleDateInput(raw: string, locale: SupportedLocale): string {
  const separator = getLocaleDateSeparator(locale);
  const digits = raw.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}${separator}${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}${separator}${digits.slice(2, 4)}${separator}${digits.slice(4)}`;
}

export function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
}

export function parseIsoCalendarDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function toIsoDateString(date: Date | null | undefined): string | null {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000Z`;
}

/**
 * Parses a plain calendar date (`yyyy-MM-dd`) into a local `Date` at midnight.
 *
 * `new Date('2026-01-01')` would be parsed as UTC midnight and can render as the previous day west of
 * Greenwich, so the parts are used to build a local date instead.
 */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? '').trim());
  if (!match) {
    return null;
  }

  return buildValidDate(Number(match[1]), Number(match[2]), Number(match[3]));
}

/** Formats a `Date` as a plain calendar date (`yyyy-MM-dd`) from its local fields. */
export function toCalendarDate(date: Date | null | undefined): string | null {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const weekdayFormatters = new Map<string, Intl.DateTimeFormat>();

/** Caches formatters: constructing an `Intl.DateTimeFormat` costs far more than reusing one. */
function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  locale: SupportedLocale,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  let formatter = cache.get(locale);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    cache.set(locale, formatter);
  }

  return formatter;
}

/** Weekday name in the given locale, for example `Thursday` / `Perşembe`. */
export function formatWeekday(date: Date, locale: SupportedLocale): string {
  return cachedFormatter(weekdayFormatters, locale, { weekday: 'long' }).format(date);
}

export function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function endOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

function buildValidDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return startOfDay(date);
}
